import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { ES } from "@/lib/es";
import { metricize } from "@/lib/units";
import { TERMS } from "@/lib/terms";

const LangContext = createContext(null);
const KEY = "fhp-lang";

// Native names, so drivers can find their own language.
export const LANGUAGES = [
  ["en", "English"], ["es", "Español"], ["fr", "Français"], ["pt", "Português"], ["de", "Deutsch"], ["it", "Italiano"],
  ["nl", "Nederlands"], ["pl", "Polski"], ["ro", "Română"], ["ru", "Русский"], ["uk", "Українська"], ["el", "Ελληνικά"],
  ["tr", "Türkçe"], ["ar", "العربية"], ["fa", "فارسی"], ["ur", "اردو"], ["he", "עברית"], ["hi", "हिन्दी"], ["pa", "ਪੰਜਾਬੀ"],
  ["bn", "বাংলা"], ["gu", "ગુજરાતી"], ["ta", "தமிழ்"], ["zh", "中文"], ["ja", "日本語"], ["ko", "한국어"], ["vi", "Tiếng Việt"],
  ["th", "ไทย"], ["id", "Bahasa Indonesia"], ["tl", "Filipino"], ["sw", "Kiswahili"], ["so", "Soomaali"], ["am", "አማርኛ"],
  ["ht", "Kreyòl ayisyen"],
];
export const LANG_NAMES = Object.fromEntries(LANGUAGES);
const CODES = new Set(LANGUAGES.map(([c]) => c));
const RTL = new Set(["ar", "fa", "ur", "he"]);

const initial = () => {
  try {
    const saved = localStorage.getItem(KEY);
    if (CODES.has(saved)) return saved;
  } catch { /* private mode */ }
  for (const l of navigator.languages || [navigator.language || ""]) {
    const code = (l || "").toLowerCase().split("-")[0];
    if (CODES.has(code)) return code;
  }
  return "en";
};

const loadDict = (code) => (code === "en" ? Promise.resolve({}) : code === "es" ? Promise.resolve(ES)
  : import(/* webpackChunkName: "locale-[request]" */ `@/lib/locales/${code}.json`).then((m) => m.default || m).catch(() => ({})));

const loadTerms = (code) => (TERMS[code] ? Promise.resolve(TERMS[code])
  : import(/* webpackChunkName: "terms-[request]" */ `@/lib/locales/terms-${code}.json`).then((m) => ({ ...TERMS.en, ...(m.default || m) })).catch(() => TERMS.en));

export function LangProvider({ children }) {
  const [lang, setLang] = useState(initial);
  const [dict, setDict] = useState(lang === "es" ? ES : {});
  const [terms, setTerms] = useState(TERMS[lang] || TERMS.en);
  useEffect(() => {
    let live = true;
    loadDict(lang).then((d) => live && setDict(d));
    loadTerms(lang).then((d) => live && setTerms(d));
    document.documentElement.lang = lang;
    document.documentElement.dir = RTL.has(lang) ? "rtl" : "ltr";
    try { localStorage.setItem(KEY, lang); } catch { /* private mode */ }
    return () => { live = false; };
  }, [lang]);
  const t = useCallback((key, params) => {
    let s = dict[key] ?? key;
    if (params) for (const [k, v] of Object.entries(params)) s = s.split(`{${k}}`).join(String(v));
    return metricize(s);
  }, [dict]);
  return <LangContext.Provider value={{ lang, setLang, t, terms }}>{children}</LangContext.Provider>;
}

export const useLang = () => useContext(LangContext);
export const useT = () => useContext(LangContext).t;
